<template>
  <div class="pricelist-page">
    <!-- HERO -->
    <section
      class="price-hero d-flex align-items-end justify-content-center text-center pb-md-5 pb-2"
    >
      <div class="container-xxl">
        <div>
          <h1 class="fw-bold title-page">Pilihan Paket Gaharu Outbound</h1>
          <p class="lead-page text-success">
            Pilih paket terbaik sesuai kebutuhan Anda
          </p>
        </div>
      </div>
    </section>

    <!-- PRICE LIST -->
    <div class="container-xxl py-5">
      <div class="cta-box mb-4 text-start">
        <div>
          <h4 class="fw-bold text-success mb-2">Butuh bantuan memilih paket?</h4>
          <p class="mb-0">Konsultasi gratis via WhatsApp dan dapatkan rekomendasi paket yang paling cocok.</p>
        </div>
        <button type="button" class="btn btn-success fw-semibold" @click="handleWhatsAppClick({ title: 'Konsultasi paket outbound', service: selectedService || 'semua paket' })">
          <i class="bi bi-whatsapp me-2"></i>
          Tanya Via WhatsApp
        </button>
      </div>

      <!-- FILTER BUTTON -->
      <div class="service-filter mb-4">
        <button
          class="filter-btn"
          :class="{ active: selectedService === '' }"
          @click="changeService('')"
        >
          Semua
        </button>

        <button
          v-for="service in services"
          :key="service"
          class="filter-btn"
          :class="{ active: selectedService === service }"
          @click="changeService(service)"
        >
          {{ service }}
        </button>
      </div>
      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-grow text-warning"></div><div class="spinner-grow text-success"></div><div class="spinner-grow text-warning"></div><div class="spinner-grow text-success"></div>
        <p class="mt-3">Memuat paket...</p>
      </div>

      <!-- Error -->
      <div v-if="error" class="text-center text-danger py-5">
        {{ error }}
      </div>

      <!-- DATA -->
      <div
        v-if="!loading && filteredPricelists.length"
        class="row g-4 p-3 p-md-4"
      >
        <div
          v-for="(pricelist, index) in filteredPricelists"
          :key="pricelist.id || index"
          class="col-lg-3 col-md-6"
        >
          <div class="price-card h-100" @click="handlePackageView(pricelist)">
            <!-- IMAGE -->
            <div class="price-img-wrapper">
              <div v-if="pricelist.is_featured" class="featured-badge">
                <i class="bi bi-star-fill me-2"></i>Populer
              </div>

              <div class="img-inner">
                <img :src="pricelist.image" class="price-img" alt="Paket" />
              </div>
            </div>

            <!-- CONTENT -->
            <div class="price-content">
              <div class="row align-items-start">
                <div class="col-7">
                  <h5 class="price-title text-start">
                    {{ pricelist.title }}
                  </h5>
                </div>

                <div class="col-5 text-end mb-2 text-bold">
                  <span class="duration-text">
                    <i class="bi bi-stopwatch me-2 text-success"></i>
                    {{ pricelist.duration || "2-3 Jam" }}
                  </span>
                </div>
              </div>

              <!-- FACILITIES -->
              <div class="feature-scroll text-start">
                <ul class="facility-list">
                  Include
                  <li
                    v-for="(item, i) in pricelist.facilities"
                    :key="i"
                    class="fst-italic my-2"
                  >
                    <i class="bi bi-check-circle-fill me-2"></i> {{ item }}
                  </li>
                </ul>
              </div>

              <!-- FOOTER -->
              <div class="price-footer">
                <div class="price-top">
                  <div v-if="pricelist.description" class="min-person">
                    <i class="bi bi-person-bounding-box me-1"></i>
                    {{ pricelist.description }}
                  </div>

                  <div class="price-value">
                    IDR {{ formatPrice(pricelist.price) }}
                    <span v-if="pricelist.unit"> / {{ pricelist.unit }}</span>
                  </div>
                </div>

                <button
                  type="button"
                  class="btn-book"
                  @click.stop="handleWhatsAppClick(pricelist)"
                >
                  <i class="bi bi-whatsapp me-2"></i>
                  Booking Sekarang
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Jika kosong -->
      <div
        v-if="!loading && !filteredPricelists.length"
        class="text-center py-5"
      >
        <p>Belum ada paket tersedia.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import { useMeta } from "@/composables/useMeta";

export default {
  setup() {
    const pricelists = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const selectedService = ref("");
    const { trackMetaEvent, buildWhatsAppLink } = useMeta();

    const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

    const fetchPriceList = async () => {
      try {
        loading.value = true;

        const response = await fetch(`${API_BASE_URL}/api/price-lists`);

        if (!response.ok) {
          throw new Error("Gagal mengambil data");
        }

        const result = await response.json();
        await new Promise((resolve) => setTimeout(resolve, 500));
        const data = result.data || result;

        pricelists.value = data.map((item) => ({
          ...item,
          facilities: item.facilities ? JSON.parse(item.facilities) : [],
        }));

        trackMetaEvent("ViewContent", {
          content_name: "Daftar Paket Outbound",
          content_category: "price-list",
          content_type: "pricelist_page",
        });
      } catch (err) {
        console.error(err);
        error.value = "Terjadi kesalahan saat memuat data.";
      } finally {
        loading.value = false;
      }
    };

    /* SERVICE LIST */

    const services = computed(() => {
      const list = pricelists.value.map((p) => p.service?.name).filter(Boolean);

      return [...new Set(list)];
    });

    /* FILTER */

    const filteredPricelists = computed(() => {
      if (!selectedService.value) {
        return pricelists.value;
      }

      return pricelists.value.filter(
        (p) => p.service?.name === selectedService.value,
      );
    });

    const changeService = (service) => {
      loading.value = true;

      setTimeout(() => {
        selectedService.value = service;

        loading.value = false;
      }, 500); // 0.5 detik
    };

    /* FORMAT PRICE */

    const formatPrice = (price) => {
      if (!price) return "0";

      if (price >= 1000000) {
        return (price / 1000000).toFixed(price % 1000000 === 0 ? 0 : 1) + "M";
      }

      if (price >= 1000) {
        return (price / 1000).toFixed(price % 1000 === 0 ? 0 : 1) + "K";
      }

      return price;
    };

    /* WHATSAPP */

    const handlePackageView = (pricelist) => {
      trackMetaEvent("ViewContent", {
        content_name: pricelist.title,
        content_category: pricelist.service?.name || "price-list",
        content_type: "pricelist_item",
      });
    };

    const handleWhatsAppClick = (pricelist) => {
      const title = pricelist?.title || "paket outbound";
      const service = pricelist?.service?.name || "";
      const link = buildWhatsAppLink({
        title,
        service,
        source: "price-list-page",
      });

      trackMetaEvent("Lead", {
        content_name: title,
        content_category: service || "price-list",
        content_type: "whatsapp_click",
      });

      window.open(link, "_blank", "noopener,noreferrer");
    };

    onMounted(fetchPriceList);

    return {
      pricelists,
      loading,
      error,
      formatPrice,
      handlePackageView,
      handleWhatsAppClick,
      selectedService,
      services,
      filteredPricelists,
      changeService,
    };
  },
};
</script>

<style scoped>
/* CTA */

.cta-box {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-radius: 16px;
  background: linear-gradient(135deg, #fff8e1, #f5fff7);
  border: 1px solid #d8f2dc;
}

/* FILTER BUTTON */

.service-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.filter-btn {
  border: none;
  background: white;
  color: #198754;

  padding: 6px 14px;
  border-radius: 5px;

  font-weight: 600;
  font-size: 14px;

  cursor: pointer;

  transition: 0.3s;
}

.filter-btn:hover {
  background: #198754;
  color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.filter-btn.active {
  background: #198754;
  color: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

/* HERO */

.price-hero {
  height: 300px;
  background: url("../../../assets/bg-home.webp") center/cover no-repeat;
}

.title-page {
  font-size: 40px;
}

.lead-page {
  font-size: 20px;
}

/* CARD */

.price-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: 0.4s;
  display: flex;
  flex-direction: column;
}

.price-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

/* IMAGE */

.price-img-wrapper {
  height: 250px;
  overflow: hidden;
  position: relative;
}

.img-inner {
  width: 100%;
  height: 100%;
  transition: transform 0.6s ease;
}

.price-card:hover .img-inner {
  transform: scale(1.08);
}

.facility-list i {
  color: #2e8b57;
}

.price-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* FEATURED */

.featured-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: #59d506;
  color: #000;
  padding: 5px 10px;
  font-size: 0.8rem;
  border-radius: 5px;
  font-weight: 600;
  z-index: 2;
}

/* CONTENT */

.price-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.price-title {
  font-weight: 700;
  margin-bottom: 15px;
  font-size: 1.2rem;
}

.duration-text {
  font-size: 0.9rem;
}

/* FACILITY */

.feature-scroll {
  height: 120px;
  overflow-y: auto;
  padding-right: 6px;
  margin-bottom: 20px;
}

.feature-scroll ul {
  list-style: none;
  padding: 0;
}

.feature-scroll li {
  font-size: 0.9rem;
  margin-bottom: 8px;
}

/* FOOTER */

.price-footer {
  margin-top: auto;
  background: linear-gradient(180deg, #f8faf8 0%, #ffffff 100%);
  padding: 15px;
  border-top: 1px solid #eee;
  border-radius: 8px;
}

.price-top {
  text-align: center;
  margin-bottom: 12px;
}

.min-person {
  font-size: 12px;
  color: #2c7f31;
  font-weight: 600;
  background: #e9f7ec;
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  margin-bottom: 6px;
}

.price-value {
  font-size: 24px;
  font-weight: 700;
  color: #111;
}

.price-value span {
  font-size: 13px;
  color: #666;
}

/* BUTTON */

.btn-book {
  display: block;
  width: 100%;
  text-align: center;
  background: linear-gradient(90deg, #25d366, #1ebc59);
  color: white;
  padding: 10px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  transition: 0.25s;
  border-color: transparent;
}

.btn-book:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  color: white;
}

@media (max-width: 768px) {
  .price-hero {
    height: 200px;
    /* font-size: smaller; */
  }

  .title-page {
    /* background-color: #198754; */
    /* margin-top: 50px; */
    font-size: 25px;
  }

  .lead-page {
    font-size: 9px;
  }
}
</style>
